import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { ChevronDown, ChevronRight, Copy01, InfoCircle, Plus, Trash01 } from "@untitledui/icons";
import { Table } from "@/components/application/table/table";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { SectionHeader } from "@/components/application/section-headers/section-headers";
import { AppDialog } from "@/components/app/AppDialog";
import { ApiError } from "@/lib/apiClient";
import { formatDateTime } from "@/lib/format";
import {
  useApiClients,
  useApiCatalog,
  useKeysForClient,
  useCreateClient,
  useDeleteClient,
  useCreateKey,
  useRevokeKey,
  type ApiClient,
  type CreatedKey,
} from "@/lib/apiKeys";

const inputCls =
  "w-full rounded-lg bg-primary px-3 py-2 text-sm text-primary shadow-xs ring-1 ring-primary outline-none focus:ring-2 focus:ring-brand";

/**
 * Schéma hlavičky, kterým se klíč posílá. Drží se v konstantě, protože stejnou
 * větu potřebuje stránka i dialog s právě vygenerovaným klíčem.
 */
const AUTH_HEADER_EXAMPLE = "Authorization: ApiKey etts_<keyId>_<secret>";

/**
 * Klíč se autentizuje schématem `ApiKey`, ne `Bearer` — `Bearer` čte JWT filtr a
 * klíč v něm skončí na 401 ještě před vyhodnocením rolí a scopes. Záměna je
 * natolik snadná (Swagger dialog nabízí obě schémata pod sebou), že se schéma
 * ukazuje přímo u seznamu klíčů, ne jen v nápovědě.
 */
function UsageHint() {
  const { t } = useTranslation();

  return (
    <div className="flex items-start gap-3 rounded-xl bg-secondary p-4 ring-1 ring-secondary">
      <InfoCircle className="mt-0.5 size-4 shrink-0 text-tertiary" aria-hidden />
      <div className="flex flex-col items-start gap-2">
        <p className="text-sm text-secondary">{t("apikeys.usage.intro")}</p>
        <code className="rounded-md bg-primary px-2 py-1 font-mono text-xs break-all text-primary ring-1 ring-secondary">
          {AUTH_HEADER_EXAMPLE}
        </code>
        <p className="text-xs text-tertiary">{t("apikeys.usage.notBearer")}</p>
        <Link
          to="/docs/$slug"
          params={{ slug: "api-klice" }}
          className="text-sm font-medium text-brand-secondary hover:underline"
        >
          {t("apikeys.usage.help")}
        </Link>
      </div>
    </div>
  );
}

function apiErrText(e: unknown): string {
  if (e instanceof ApiError) {
    const body = e.body as { message?: string } | string | null;
    if (typeof body === "string" && body) return body;
    if (body && typeof body === "object" && body.message) return body.message;
  }
  return e instanceof Error ? e.message : String(e);
}

export function ApiKeysPage() {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useApiClients();
  const [creating, setCreating] = useState(false);
  const [deleteClient, setDeleteClient] = useState<ApiClient | null>(null);

  return (
    <div className="flex flex-col gap-6">
      <SectionHeader.Root>
        <SectionHeader.Group>
          <div className="flex-1">
            <SectionHeader.Heading>{t("apikeys.title")}</SectionHeader.Heading>
            <SectionHeader.Subheading>{t("apikeys.subtitle")}</SectionHeader.Subheading>
          </div>
          <SectionHeader.Actions>
            <Button size="md" color="primary" iconLeading={Plus} onPress={() => setCreating(true)}>
              {t("apikeys.addClient")}
            </Button>
          </SectionHeader.Actions>
        </SectionHeader.Group>
      </SectionHeader.Root>

      <UsageHint />

      {isLoading && <LoadingIndicator />}
      {isError && <p className="text-sm text-error-primary">{t("common.loadError")}</p>}

      {data && (
        <div className="flex flex-col gap-4">
          {data.length === 0 && <p className="text-sm text-tertiary">{t("apikeys.noClients")}</p>}
          {data.map((c) => (
            <ClientCard key={c.id} client={c} onDelete={() => setDeleteClient(c)} />
          ))}
        </div>
      )}

      {creating && <CreateClientModal onClose={() => setCreating(false)} />}
      {deleteClient && <DeleteClientModal client={deleteClient} onClose={() => setDeleteClient(null)} />}
    </div>
  );
}

function ClientCard({ client, onDelete }: { client: ApiClient; onDelete: () => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [addKey, setAddKey] = useState(false);
  const keys = useKeysForClient(client.id, open);
  const revoke = useRevokeKey(client.id);

  return (
    <div className="rounded-xl bg-primary ring-1 ring-secondary">
      <div className="flex flex-wrap items-center justify-between gap-3 p-4">
        <div className="flex items-center gap-3">
          <Button size="sm" color="tertiary" iconLeading={open ? ChevronDown : ChevronRight} onPress={() => setOpen((o) => !o)} />
          <div>
            <span className="text-md font-semibold text-primary">{client.name}</span>
            {client.description && <p className="text-xs text-tertiary">{client.description}</p>}
            {client.createdAt && (
              <p className="text-xs text-tertiary">
                {t("apikeys.created")}: {formatDateTime(client.createdAt)}
              </p>
            )}
          </div>
          <Badge type="pill-color" size="sm" color={client.enabled ? "success" : "gray"}>
            {client.enabled ? t("apikeys.enabled") : t("apikeys.disabled")}
          </Badge>
        </div>
        <div className="flex gap-2">
          <Button size="sm" color="secondary" iconLeading={Plus} onPress={() => setAddKey(true)}>{t("apikeys.addKey")}</Button>
          <Button size="sm" color="tertiary" iconLeading={Trash01} onPress={onDelete} aria-label={t("common.delete")} />
        </div>
      </div>

      {open && (
        <div className="border-t border-secondary p-4">
          {keys.isLoading && <LoadingIndicator />}
          {keys.data && keys.data.length === 0 && <p className="text-sm text-tertiary">{t("apikeys.noKeys")}</p>}
          {keys.data && keys.data.length > 0 && (
            <Table aria-label={t("apikeys.keys")} selectionMode="none">
              <Table.Header>
                <Table.Head isRowHeader id="keyId" label="Key ID" />
                <Table.Head id="label" label={t("apikeys.label")} />
                <Table.Head id="roles" label={t("apikeys.roles")} />
                <Table.Head id="scopes" label={t("apikeys.scopes")} />
                <Table.Head id="status" label={t("users.col.status")} />
                <Table.Head id="lastUsedAt" label={t("apikeys.lastUsed")} />
                <Table.Head id="expiresAt" label={t("tokens.field.expires")} />
                <Table.Head id="actions" label="" />
              </Table.Header>
              <Table.Body items={keys.data}>
                {(k) => (
                  <Table.Row id={k.id}>
                    <Table.Cell className="font-mono text-xs">{k.keyId}</Table.Cell>
                    <Table.Cell>{k.label ?? "—"}</Table.Cell>
                    <Table.Cell>{k.roles.length > 0 ? k.roles.join(", ") : "—"}</Table.Cell>
                    <Table.Cell className="max-w-xs truncate text-xs text-tertiary" title={k.scopes.join(", ")}>
                      {k.scopes.length > 0 ? k.scopes.join(", ") : "—"}
                    </Table.Cell>
                    <Table.Cell>
                      <Badge type="pill-color" size="sm" color={k.revokedAt ? "error" : k.enabled ? "success" : "gray"}>
                        {k.revokedAt ? t("apikeys.revoked") : k.enabled ? t("apikeys.active") : t("apikeys.disabled")}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>{formatDateTime(k.lastUsedAt)}</Table.Cell>
                    <Table.Cell>{formatDateTime(k.expiresAt)}</Table.Cell>
                    <Table.Cell>
                      {!k.revokedAt && (
                        <div className="flex justify-end">
                          <Button size="sm" color="tertiary" isDisabled={revoke.isPending} onPress={() => void revoke.mutateAsync(k.id)}>
                            {t("apikeys.revoke")}
                          </Button>
                        </div>
                      )}
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          )}
        </div>
      )}

      {addKey && <CreateKeyModal clientId={client.id} onClose={() => setAddKey(false)} />}
    </div>
  );
}

function CreateClientModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const create = useCreateClient();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setErr(null);
    if (!name.trim()) return setErr(t("apikeys.nameRequired"));
    try {
      await create.mutateAsync({ name: name.trim(), description: description.trim() || undefined });
      onClose();
    } catch (e) {
      setErr(apiErrText(e));
    }
  };

  return (
    <AppDialog
      isOpen
      onClose={onClose}
      title={t("apikeys.addClient")}
      footer={
        <>
          <Button color="secondary" onPress={onClose} isDisabled={create.isPending}>{t("common.cancel")}</Button>
          <Button color="primary" onPress={() => void submit()} isDisabled={create.isPending}>{t("common.save")}</Button>
        </>
      }
    >
      {err && <div className="mb-3 rounded-lg bg-error-primary px-3 py-2 text-sm text-error-primary ring-1 ring-error">{err}</div>}
      <div className="flex flex-col gap-4">
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-secondary">{t("apikeys.name")}</span>
          <input className={inputCls} autoFocus value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-secondary">{t("apikeys.description")}</span>
          <input className={inputCls} value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>
      </div>
    </AppDialog>
  );
}

function CreateKeyModal({ clientId, onClose }: { clientId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const catalog = useApiCatalog();
  const create = useCreateKey(clientId);
  const [label, setLabel] = useState("");
  const [roles, setRoles] = useState<string[]>([]);
  const [scopes, setScopes] = useState<string[]>([]);
  const [ttlDays, setTtlDays] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedKey | null>(null);

  const scopesByModule = useMemo(() => {
    const map = new Map<string, { id: string; description: string }[]>();
    (catalog.data?.scopes ?? []).forEach((s) => {
      const arr = map.get(s.module) ?? [];
      arr.push({ id: s.id, description: s.description });
      map.set(s.module, arr);
    });
    return [...map.entries()];
  }, [catalog.data]);

  const toggle = (arr: string[], v: string, set: (x: string[]) => void) =>
    set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const submit = async () => {
    setErr(null);
    if (roles.length === 0 && scopes.length === 0) return setErr(t("apikeys.needScopeOrRole"));
    try {
      const res = await create.mutateAsync({
        label: label.trim() || undefined,
        scopes,
        roles,
        ttlDays: ttlDays ? Number(ttlDays) : undefined,
      });
      setCreated(res);
    } catch (e) {
      setErr(apiErrText(e));
    }
  };

  const copy = () => {
    if (created && navigator.clipboard) void navigator.clipboard.writeText(created.plaintextValue);
  };

  return (
    <AppDialog
      isOpen
      onClose={onClose}
      title={t("apikeys.newKey")}
      footer={
        created ? (
          <Button color="primary" onPress={onClose}>{t("common.done")}</Button>
        ) : (
          <>
            <Button color="secondary" onPress={onClose} isDisabled={create.isPending}>{t("common.cancel")}</Button>
            <Button color="primary" onPress={() => void submit()} isDisabled={create.isPending}>{t("apikeys.generate")}</Button>
          </>
        )
      }
    >
      {created ? (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-tertiary">{t("apikeys.copyNow")}</p>
          <div className="flex items-center gap-2 rounded-lg bg-secondary p-3 ring-1 ring-secondary">
            <code className="flex-1 break-all font-mono text-xs text-primary">{created.plaintextValue}</code>
            <Button size="sm" color="secondary" iconLeading={Copy01} onPress={copy}>{t("apikeys.copy")}</Button>
          </div>
          {/* Schéma se opakuje i tady — klíč se odsud kopíruje rovnou do klienta,
              takže je to poslední okamžik, kdy má smysl na `ApiKey` upozornit. */}
          <p className="text-xs text-tertiary">{t("apikeys.usage.modalHint")}</p>
        </div>
      ) : (
        <div className="flex max-h-[60vh] flex-col gap-4 overflow-y-auto">
          {err && <div className="rounded-lg bg-error-primary px-3 py-2 text-sm text-error-primary ring-1 ring-error">{err}</div>}
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-secondary">{t("apikeys.label")}</span>
            <input className={inputCls} value={label} onChange={(e) => setLabel(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-secondary">{t("apikeys.ttl")}</span>
            <input className={inputCls} type="number" min={1} placeholder={t("apikeys.ttlNever")} value={ttlDays} onChange={(e) => setTtlDays(e.target.value)} />
          </label>
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-secondary">{t("apikeys.roles")}</span>
            <div className="flex flex-wrap gap-3">
              {(catalog.data?.roles ?? []).map((r) => (
                <label key={r.code} className="flex items-center gap-2 text-sm text-primary" title={r.description}>
                  <input type="checkbox" checked={roles.includes(r.code)} onChange={() => toggle(roles, r.code, setRoles)} />
                  {r.label}
                </label>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-secondary">{t("apikeys.scopes")}</span>
            {scopesByModule.map(([module, list]) => (
              <div key={module} className="rounded-lg bg-secondary p-2 ring-1 ring-secondary">
                <p className="mb-1 text-xs font-semibold text-tertiary">{module}</p>
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  {list.map((s) => (
                    <label key={s.id} className="flex items-center gap-2 text-sm text-primary" title={s.description}>
                      <input type="checkbox" checked={scopes.includes(s.id)} onChange={() => toggle(scopes, s.id, setScopes)} />
                      {s.id}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </AppDialog>
  );
}

function DeleteClientModal({ client, onClose }: { client: ApiClient; onClose: () => void }) {
  const { t } = useTranslation();
  const del = useDeleteClient();
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    setErr(null);
    try {
      await del.mutateAsync(client.id);
      onClose();
    } catch (e) {
      setErr(apiErrText(e));
    }
  };

  return (
    <AppDialog
      isOpen
      onClose={onClose}
      title={t("apikeys.deleteClientTitle")}
      description={t("apikeys.deleteClientConfirm", { name: client.name })}
      footer={
        <>
          <Button color="secondary" onPress={onClose} isDisabled={del.isPending}>{t("common.cancel")}</Button>
          <Button color="primary-destructive" onPress={() => void submit()} isDisabled={del.isPending}>{t("common.delete")}</Button>
        </>
      }
    >
      {err && <div className="rounded-lg bg-error-primary px-3 py-2 text-sm text-error-primary ring-1 ring-error">{err}</div>}
    </AppDialog>
  );
}
